import { alignMaxMatchCost, alignSkipCost } from './constants.js'
import type { SungNote } from './types.js'

export interface AlignedPair {
  /** Index into the sung note array. */
  sungIndex: number
  /** Index into the reference MIDI array. */
  referenceIndex: number
}

export interface Alignment {
  pairs: AlignedPair[]
  /** Indices into `reference` of notes the singer did not sing. */
  missed: number[]
  /** Indices into `sung` of notes that are not in the reference. */
  extra: number[]
}

/**
 * Align the sung note sequence to the reference sequence over the *note sequences*, not over
 * clock time (§5.3). Singers do not hold tempo, and this is a pitch tool — penalising a slow
 * singer for being slow would measure the wrong thing.
 *
 * The insertion and deletion paths are the point: they give the two error categories that
 * matter musically and are otherwise invisible — "you missed a note" and "you sang an extra
 * note".
 */
export const alignNotes = (sung: SungNote[], reference: number[]): Alignment => {
  const rows = sung.length
  const columns = reference.length

  const matchCost = (sungIndex: number, referenceIndex: number) =>
    Math.min(Math.abs(sung[sungIndex].midi - reference[referenceIndex]), alignMaxMatchCost)

  /** cost[i][j] = cheapest alignment of the first i sung notes to the first j reference notes. */
  const cost: number[][] = Array.from({ length: rows + 1 }, () => new Array<number>(columns + 1).fill(0))
  for (let i = 1; i <= rows; i++) {
    cost[i][0] = i * alignSkipCost
  }
  for (let j = 1; j <= columns; j++) {
    cost[0][j] = j * alignSkipCost
  }
  for (let i = 1; i <= rows; i++) {
    for (let j = 1; j <= columns; j++) {
      cost[i][j] = Math.min(
        cost[i - 1][j - 1] + matchCost(i - 1, j - 1),
        cost[i - 1][j] + alignSkipCost,
        cost[i][j - 1] + alignSkipCost
      )
    }
  }

  const pairs: AlignedPair[] = []
  const missed: number[] = []
  const extra: number[] = []

  let i = rows
  let j = columns
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && cost[i][j] === cost[i - 1][j - 1] + matchCost(i - 1, j - 1)) {
      pairs.push({ sungIndex: i - 1, referenceIndex: j - 1 })
      i--
      j--
      continue
    }
    if (i > 0 && cost[i][j] === cost[i - 1][j] + alignSkipCost) {
      extra.push(i - 1)
      i--
      continue
    }
    missed.push(j - 1)
    j--
  }

  return { pairs: pairs.reverse(), missed: missed.reverse(), extra: extra.reverse() }
}
